import { useTranslation } from "react-i18next";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import DeleteIcon from "@mui/icons-material/Delete";
import DragHandleIcon from "@mui/icons-material/DragHandle";
import AddIcon from "@mui/icons-material/Add";
import { FieldArray } from "react-final-form-arrays";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import type { DropResult } from "@hello-pangea/dnd";
import { TextField } from "@/shared/components/Form/TextField";
import { AutocompleteField } from "@/shared/components/Form/AutocompleteField";
import { Switch } from "@/shared/components/Form/Switch";
import { ImageField } from "@/shared/components/Form/ImageField";
import type {
  PlatformBlockComponent,
  PlatformBlockProps,
} from "../../../types";
import { BlockFormHeader } from "../BlockFormHeader";

const getListStyle = (isDraggingOver: boolean): React.CSSProperties => ({
  border: "1px solid rgba(255,255,255,0.1)",
  borderRadius: 4,
  background: isDraggingOver ? "#212121" : "transparent",
  width: "100%",
});

const getItemStyle = (
  isDragging: boolean,
  draggableStyle: React.CSSProperties | undefined,
  last: boolean
): React.CSSProperties => ({
  display: "flex",
  userSelect: "none",
  borderBottom: isDragging || last ? "none" : "1px solid rgba(255,255,255,0.1)",
  padding: "16px 8px",
  ...draggableStyle,
});

/**
 * Блок партнёров: список логотипов со ссылками. Логотипы хранятся как аватары
 * (`logoId`) — так их читает PartnersBlock на сайте.
 */
export const Partners: PlatformBlockComponent = ({
  name,
}: PlatformBlockProps) => {
  const { t } = useTranslation();

  const viewOptions = [
    { label: t("platformBlocks.view.CAROUSEL"), value: "CAROUSEL" },
    { label: t("platformBlocks.view.GRID"), value: "GRID" },
  ];

  // Как в референсе: выравнивание подписи под логотипом
  const alignOptions = [
    { label: t("platformBlocks.align.left"), value: "left" },
    { label: t("platformBlocks.align.center"), value: "center" },
    { label: t("platformBlocks.align.right"), value: "right" },
  ];

  const makeOnDragEndFunction = (fields: any) => (result: DropResult) => {
    if (!result.destination) {
      return;
    }
    fields.move(result.source.index, result.destination.index);
  };

  return (
    <Paper sx={{ p: 2 }}>
      <BlockFormHeader blockType="PARTNERS" />
      <Grid container spacing={2}>
        <Grid size={6}>
          <TextField
            name={`${name}.title`}
            label={t("platformBlocks.fields.title")}
            type="text"
            fullWidth
          />
        </Grid>
        <Grid size={6}>
          <AutocompleteField
            name={`${name}.view`}
            label={t("platformBlocks.fields.displayView")}
            fullWidth
            options={viewOptions}
          />
        </Grid>
        <Grid size={12}>
          <FieldArray name={`${name}.logos`}>
            {({ fields }) => (
              <DragDropContext onDragEnd={makeOnDragEndFunction(fields)}>
                <Droppable droppableId="partners">
                  {(provided, snapshot) => (
                    <div>
                      <div
                        ref={provided.innerRef}
                        style={getListStyle(snapshot.isDraggingOver)}
                      >
                        {fields.map((fieldName, index) => (
                          <Draggable
                            key={fieldName}
                            draggableId={fieldName}
                            index={index}
                          >
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                style={getItemStyle(
                                  snapshot.isDragging,
                                  provided.draggableProps.style,
                                  index + 1 === fields.length
                                )}
                              >
                                <Grid container spacing={2}>
                                  <Grid size={4}>
                                    <ImageField
                                      name={`${fieldName}.logoId`}
                                      imageType="avatar"
                                      label={t("platformBlocks.fields.logo")}
                                      width={120}
                                      height={120}
                                      required
                                    />
                                  </Grid>
                                  <Grid size={7}>
                                    <Grid container spacing={2}>
                                      <Grid size={12}>
                                        <TextField
                                          name={`${fieldName}.title`}
                                          label={t(
                                            "platformBlocks.fields.name"
                                          )}
                                          type="text"
                                          fullWidth
                                        />
                                      </Grid>
                                      <Grid size={12}>
                                        <TextField
                                          name={`${fieldName}.url`}
                                          label={t(
                                            "platformBlocks.fields.link"
                                          )}
                                          type="text"
                                          fullWidth
                                        />
                                      </Grid>
                                      <Grid size={12}>
                                        <AutocompleteField
                                          name={`${fieldName}.align`}
                                          label={t(
                                            "platformBlocks.fields.nameAlign"
                                          )}
                                          fullWidth
                                          options={alignOptions}
                                        />
                                      </Grid>
                                      <Grid>
                                        <Switch
                                          name={`${fieldName}.openInNewWindow`}
                                          label={t(
                                            "platformBlocks.fields.openInNewWindow"
                                          )}
                                        />
                                      </Grid>
                                    </Grid>
                                  </Grid>
                                  <Grid
                                    sx={{
                                      display: "flex",
                                      flexDirection: "column",
                                      alignItems: "center",
                                    }}
                                    size={1}
                                  >
                                    <IconButton
                                      type="button"
                                      onClick={() => fields.remove(index)}
                                      size="large"
                                    >
                                      <DeleteIcon />
                                    </IconButton>
                                    <IconButton {...provided.dragHandleProps}>
                                      <DragHandleIcon />
                                    </IconButton>
                                  </Grid>
                                </Grid>
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                      <div style={{ marginTop: 16 }}>
                        <Button
                          onClick={() => fields.push({})}
                          fullWidth
                          variant="outlined"
                          color="secondary"
                        >
                          <AddIcon /> {t("platformBlocks.partners.addPartner")}
                        </Button>
                      </div>
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            )}
          </FieldArray>
        </Grid>
      </Grid>
    </Paper>
  );
};
